import type { ComponentType, ReactNode } from 'react'
import type { LucideProps } from 'lucide-react'
import { cn } from '@/lib/utils'

type Tone = 'royal' | 'periwinkle' | 'violet' | 'amber' | 'green' | 'rose' | 'neutral'

const TONE_BG: Record<Tone, string> = {
  royal: 'from-brand-royal/8 via-card to-card',
  periwinkle: 'from-brand-periwinkle/8 via-card to-card',
  violet: 'from-violet-500/8 via-card to-card',
  amber: 'from-amber-500/8 via-card to-card',
  green: 'from-emerald-500/8 via-card to-card',
  rose: 'from-rose-500/8 via-card to-card',
  neutral: 'from-muted via-card to-card',
}

const TONE_BORDER: Record<Tone, string> = {
  royal: 'border-brand-royal/25',
  periwinkle: 'border-brand-periwinkle/25',
  violet: 'border-violet-500/25',
  amber: 'border-amber-500/25',
  green: 'border-emerald-500/25',
  rose: 'border-rose-500/25',
  neutral: 'border-border',
}

const TONE_ICON: Record<Tone, { wrap: string; icon: string }> = {
  royal:      { wrap: 'from-brand-royal/20 to-brand-royal/5 border-brand-royal/30',          icon: 'text-brand-royal' },
  periwinkle: { wrap: 'from-brand-periwinkle/20 to-brand-periwinkle/5 border-brand-periwinkle/30', icon: 'text-brand-periwinkle' },
  violet:     { wrap: 'from-violet-500/20 to-violet-500/5 border-violet-500/30',             icon: 'text-violet-500' },
  amber:      { wrap: 'from-amber-500/20 to-amber-500/5 border-amber-500/30',                icon: 'text-amber-600 dark:text-amber-400' },
  green:      { wrap: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',          icon: 'text-emerald-600 dark:text-emerald-400' },
  rose:       { wrap: 'from-rose-500/20 to-rose-500/5 border-rose-500/30',                   icon: 'text-rose-500' },
  neutral:    { wrap: 'from-muted to-muted/50 border-border',                                 icon: 'text-muted-foreground' },
}

interface EmptyStateProps {
  /** Required headline */
  title: string
  /** Optional one or two-line body */
  description?: string
  /** Lucide icon (rendered in a gradient chip) */
  icon?: ComponentType<LucideProps>
  /** Or pass a custom illustration node (overrides icon) */
  illustration?: ReactNode
  /** Action cluster — primary button on left, optional secondary on right */
  actions?: ReactNode
  tone?: Tone
  /** Compact variant — smaller padding for sidebar/inline empty states */
  compact?: boolean
  className?: string
}

/**
 * Replaces the lonely-icon-and-grey-text empty states.
 * Visual: gradient backdrop tinted by tone, gradient ring around the icon
 * chip, generous padding, primary CTA + optional secondary action.
 */
export function EmptyState({
  title,
  description,
  icon: Icon,
  illustration,
  actions,
  tone = 'neutral',
  compact = false,
  className,
}: EmptyStateProps) {
  const ic = TONE_ICON[tone]
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border shadow-sm card-inset bg-gradient-to-br',
        TONE_BG[tone],
        TONE_BORDER[tone],
        className,
      )}
    >
      {/* Decorative blurred orb */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full opacity-40 blur-3xl"
        style={{ background: `radial-gradient(circle, currentColor 0%, transparent 70%)` }}
      />
      <div className={cn('relative text-center', compact ? 'p-6' : 'p-8 sm:p-10')}>
        {illustration ? (
          <div className="mx-auto mb-4 sm:mb-5 max-w-[12rem]">{illustration}</div>
        ) : Icon ? (
          <div
            className={cn(
              'mx-auto rounded-2xl bg-gradient-to-br border flex items-center justify-center mb-4 sm:mb-5',
              compact ? 'h-12 w-12' : 'h-16 w-16',
              ic.wrap,
            )}
          >
            <Icon className={cn(compact ? 'h-5 w-5' : 'h-7 w-7', ic.icon)} />
          </div>
        ) : null}
        <h3 className={cn('font-heading font-semibold tracking-tight', compact ? 'text-base' : 'text-lg')}>{title}</h3>
        {description && (
          <p className={cn('text-sm text-muted-foreground mt-1.5 max-w-md mx-auto', compact && 'text-xs')}>
            {description}
          </p>
        )}
        {actions && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">{actions}</div>
        )}
      </div>
    </div>
  )
}
